import { medalPoints } from "@dailywars/core/medals";
import { useVirtualizer } from "@tanstack/react-virtual";
import { useEffect, useRef } from "react";
import type * as React from "react";

import { MedalIcon } from "#/components/MedalIcon";
import { PlayerPreviewName } from "#/components/PlayerPreviewName";
import { Card, CardContent } from "#/components/ui/card";
import { Empty, EmptyDescription, EmptyHeader, EmptyTitle } from "#/components/ui/empty";
import { ScrollArea } from "#/components/ui/scroll-area";
import { Spinner } from "#/components/ui/spinner";

export type HiscoreRow = {
  playerId: string;
  displayName: string;
  goldMedals: number;
  silverMedals: number;
  bronzeMedals: number;
};

const ROW_ESTIMATE_PX = 48;

function HiscoresPendingCard() {
  return (
    <Card className="w-full overflow-hidden">
      <CardContent className="flex items-center justify-center py-16">
        <Spinner />
      </CardContent>
    </Card>
  );
}

function HiscoresEmptyCard() {
  return (
    <Card className="w-full overflow-hidden">
      <CardContent className="py-12">
        <Empty className="border-0 p-0">
          <EmptyHeader>
            <EmptyTitle>No medals yet</EmptyTitle>
            <EmptyDescription>
              Medals are awarded shortly after each UTC midnight cutoff.
            </EmptyDescription>
          </EmptyHeader>
        </Empty>
      </CardContent>
    </Card>
  );
}

function useHiscoresPrefetchNextPage({
  scrollViewportRef,
  virtualItems,
  flatRowsLength,
  hasNextPage,
  isFetchingNextPage,
  fetchNextPage,
}: {
  scrollViewportRef: React.RefObject<HTMLDivElement | null>;
  virtualItems: ReturnType<
    ReturnType<typeof useVirtualizer<HTMLDivElement, Element>>["getVirtualItems"]
  >;
  flatRowsLength: number;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  fetchNextPage: () => unknown;
}) {
  const lastVisibleIndex = virtualItems[virtualItems.length - 1]?.index;
  useEffect(() => {
    if (!scrollViewportRef.current) return;
    if (lastVisibleIndex === undefined) return;
    if (lastVisibleIndex >= flatRowsLength - 5 && hasNextPage && !isFetchingNextPage) {
      void fetchNextPage();
    }
  }, [
    scrollViewportRef,
    lastVisibleIndex,
    flatRowsLength,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  ]);
}

function HiscoresMedalCount({
  tier,
  count,
}: {
  tier: "gold" | "silver" | "bronze";
  count: number;
}) {
  if (count <= 0) return null;
  return (
    <span className="inline-flex items-center gap-1 text-muted-foreground">
      <MedalIcon tier={tier} size={16} />
      {count}
    </span>
  );
}

function HiscoresRowStats({ entry }: { entry: HiscoreRow }) {
  const hasMedals = entry.goldMedals > 0 || entry.silverMedals > 0 || entry.bronzeMedals > 0;
  return (
    <span className="flex shrink-0 items-center gap-1.5 tabular-nums">
      <HiscoresMedalCount tier="gold" count={entry.goldMedals} />
      <HiscoresMedalCount tier="silver" count={entry.silverMedals} />
      <HiscoresMedalCount tier="bronze" count={entry.bronzeMedals} />
      {hasMedals ? <span aria-hidden="true" className="mx-0.5 h-4 w-px bg-border sm:mx-1" /> : null}
      <span className="w-[4ch] shrink-0 text-right font-medium text-foreground">
        {medalPoints(entry)}p
      </span>
    </span>
  );
}

function HiscoresVirtualRow({
  virtualRow,
  entry,
}: {
  virtualRow: { key: React.Key; index: number; size: number; start: number };
  entry: HiscoreRow;
}) {
  const rank = virtualRow.index + 1;
  return (
    <div
      key={virtualRow.key}
      className="absolute left-0 top-0 flex w-full items-center justify-between gap-3 border-b border-border/60 px-4 py-3 text-sm"
      style={{
        height: `${virtualRow.size}px`,
        transform: `translateY(${virtualRow.start}px)`,
      }}
    >
      <span className="min-w-0 flex-1 truncate">
        <span className="tabular-nums text-muted-foreground">{rank}. </span>
        <PlayerPreviewName playerId={entry.playerId} displayName={entry.displayName} />
      </span>
      <HiscoresRowStats entry={entry} />
    </div>
  );
}

function HiscoresFetchMoreFooter() {
  return (
    <div className="flex items-center justify-center gap-2 border-t border-border py-3 text-xs text-muted-foreground">
      <Spinner />
      <span>Loading more…</span>
    </div>
  );
}

function HiscoresFilledCard({
  rows,
  hasNextPage,
  isFetchingNextPage,
  fetchNextPage,
}: {
  rows: HiscoreRow[];
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  fetchNextPage: () => unknown;
}) {
  const scrollViewportRef = useRef<HTMLDivElement>(null);
  const rowVirtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => scrollViewportRef.current,
    estimateSize: () => ROW_ESTIMATE_PX,
    overscan: 12,
  });
  const virtualItems = rowVirtualizer.getVirtualItems();

  useHiscoresPrefetchNextPage({
    scrollViewportRef,
    virtualItems,
    flatRowsLength: rows.length,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  });

  return (
    <Card className="w-full overflow-hidden">
      <CardContent className="p-0 text-left">
        <ScrollArea
          viewportRef={scrollViewportRef}
          className="h-[min(70vh,560px)] rounded-[inherit]"
        >
          <div className="relative w-full" style={{ height: `${rowVirtualizer.getTotalSize()}px` }}>
            {virtualItems.map((virtualRow) => {
              const entry = rows[virtualRow.index];
              if (!entry) return null;
              return (
                <HiscoresVirtualRow key={virtualRow.key} virtualRow={virtualRow} entry={entry} />
              );
            })}
          </div>
        </ScrollArea>
      </CardContent>
      {isFetchingNextPage ? <HiscoresFetchMoreFooter /> : null}
    </Card>
  );
}

export function HiscoresListCard({
  isPending,
  rows,
  hasNextPage,
  isFetchingNextPage,
  fetchNextPage,
}: {
  isPending: boolean;
  rows: HiscoreRow[];
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  fetchNextPage: () => unknown;
}) {
  if (isPending) {
    return <HiscoresPendingCard />;
  }
  if (rows.length === 0) {
    return <HiscoresEmptyCard />;
  }
  return (
    <HiscoresFilledCard
      rows={rows}
      hasNextPage={hasNextPage}
      isFetchingNextPage={isFetchingNextPage}
      fetchNextPage={fetchNextPage}
    />
  );
}
